import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Zap, PauseCircle } from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";

export default function KillSwitchModal({ onClose }) {
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState(null);

  const handleCloseAll = async () => {
    setLoading("close");
    try {
      const { data } = await axios.post(`${API}/positions/kill/close-all`);
      setResult({ type: "success", msg: data.message });
    } catch (e) {
      setResult({ type: "error", msg: e.response?.data?.detail || "Failed" });
    } finally {
      setLoading(null);
    }
  };

  const handlePauseAll = async () => {
    setLoading("pause");
    try {
      const { data } = await axios.post(`${API}/positions/kill/pause-all`);
      setResult({ type: "success", msg: data.message });
    } catch (e) {
      setResult({ type: "error", msg: e.response?.data?.detail || "Failed" });
    } finally {
      setLoading(null);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="octa-clip p-6 w-full max-w-md"
          style={{ background: "linear-gradient(180deg, #161920 0%, #111318 100%)", border: "1px solid rgba(239,68,68,0.15)" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="kill-switch-modal"
        >
          <div className="flex items-start justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 octa-clip-sm flex items-center justify-center" style={{ background: "rgba(239,68,68,0.08)" }}>
                <AlertTriangle size={18} className="text-[#EF4444]" />
              </div>
              <div>
                <h2 style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", color: "#E2E8F0", fontSize: "14px" }}>Kill Switch</h2>
                <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontSize: "11px", color: "#475569" }}>Emergency trading controls</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="kill-switch-close" className="text-[#475569] hover:text-white transition-colors">
              <X size={16} />
            </button>
          </div>

          {result ? (
            <div className="octa-clip-sm p-4 mb-4" style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: "11px",
              background: result.type === "success" ? "rgba(34,197,94,0.06)" : "rgba(239,68,68,0.06)",
              color: result.type === "success" ? "#22C55E" : "#EF4444",
              border: `1px solid ${result.type === "success" ? "rgba(34,197,94,0.15)" : "rgba(239,68,68,0.15)"}`,
            }}>
              {result.msg}
            </div>
          ) : (
            <p style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#94A3B8", fontSize: "13px", marginBottom: "20px" }}>
              Choose an emergency action. These operations affect ALL platforms and positions.
            </p>
          )}

          <div className="space-y-3">
            <button
              onClick={handleCloseAll}
              disabled={!!loading}
              data-testid="kill-close-all-btn"
              className="w-full octa-clip-sm p-3 flex items-center gap-3 transition-all duration-150 disabled:opacity-50"
              style={{
                background: "rgba(239,68,68,0.06)",
                border: "1px solid rgba(239,68,68,0.2)",
                color: "#EF4444",
              }}
            >
              <Zap size={15} />
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {loading === "close" ? "Closing..." : "Emergency Close All Positions"}
              </span>
            </button>
            <button
              onClick={handlePauseAll}
              disabled={!!loading}
              data-testid="kill-pause-all-btn"
              className="w-full octa-clip-sm p-3 flex items-center gap-3 transition-all duration-150 disabled:opacity-50"
              style={{
                background: "rgba(245,158,11,0.06)",
                border: "1px solid rgba(245,158,11,0.15)",
                color: "#F59E0B",
              }}
            >
              <PauseCircle size={15} />
              <span style={{ fontFamily: "'Inter Tight', sans-serif", fontWeight: "600", fontSize: "11px", letterSpacing: "0.1em", textTransform: "uppercase" }}>
                {loading === "pause" ? "Pausing..." : "Pause All Platform Syncs"}
              </span>
            </button>
            <button
              onClick={onClose}
              data-testid="kill-cancel-btn"
              className="w-full py-2 transition-colors"
              style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#475569", fontSize: "12px" }}
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
